import { defineRouter } from "#q-app/wrappers";
import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  createWebHistory,
} from "vue-router";
import routes from "./routes";
import { getSubject, hasSalesGroup } from "src/services/AppAbility";
import { LocalStorage, Platform } from "quasar";
import { ability } from "src/boot/ability";
import { useSessionStore } from "src/stores/session";

// Hash-mode SPA: collapse any non-root path so we don't end up with URLs like
// "/orders#/photos". The hash is the only thing that should drive routing.
if (window.location.pathname !== "/") {
  window.location.replace("/" + window.location.hash);
}

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default defineRouter(function (/* { store, ssrContext } */) {
  const STORED_HISTORY_KEY = "mes:historyKey";

  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === "history"
      ? createWebHistory
      : createWebHashHistory;

  const Router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(process.env.VUE_ROUTER_BASE),
  });

  Router.beforeEach(async (to, from, next) => {
    const sessionStore = useSessionStore();
    await sessionStore.restore();

    const isAuthed = sessionStore.tokens != undefined;
    const isPublic = to.meta.requiresAuth === false;

    // Unauthed user → only public routes; everything else goes to login
    if (!isAuthed) {
      isPublic ? next() : next({ name: getSubject("Login") });
      return;
    }

    // Authed user landing on /auth/login → bounce to Home (sales get redirected to Photos from there)
    if (to.name === getSubject("Login")) {
      next({ name: getSubject("Home") });
      return;
    }

    // Sales users don't have a Home page — redirect them to Photos
    if (to.name === getSubject("Home") && hasSalesGroup(sessionStore.getUserGroups)) {
      next({ name: getSubject("Photos") });
      return;
    }

    // Deny access to subjects the user can't read
    if (!isPublic && to.meta.subject && !ability.can("read", to.meta.subject)) {
      next({ name: getSubject("Forbidden") });
      return;
    }

    // Native mobile: prefer last-visited path right after login
    if (
      from.name?.toString() === getSubject("Login") &&
      (Platform.is.nativeMobile ?? false)
    ) {
      const storedPath = LocalStorage.getItem<string>(STORED_HISTORY_KEY);
      LocalStorage.remove(STORED_HISTORY_KEY);
      if (storedPath && to.fullPath !== storedPath) {
        const resolved = Router.resolve(storedPath);
        const subject = resolved.meta.subject;
        const isValidRoute = resolved.matched.length > 0 && !resolved.matched[0]?.path.includes(":catchAll");
        // Only redirect if the stored path is valid and the user has access to it; otherwise, just go to Home
        if (isValidRoute && (!subject || ability.can("read", subject))) {
          next(storedPath);
        } else {
          next({ name: getSubject("Home") });
        }
        return;
      }
    }

    next();
  });

  Router.afterEach((to) => {
    if (to.meta.storeHistory == true && to.name) {
      LocalStorage.set(STORED_HISTORY_KEY, to.fullPath);
    }
  });

  return Router;
});
